/* eslint-disable max-len */
import React, {
  useEffect,
  useState,
} from 'react';
import {
  Box,
  Container,
  Tab,
  Tabs,
  makeStyles,
} from '@material-ui/core';
import { useLocation } from 'react-router-dom';
import Page from '../../../components/Layout/Page';
import Header from './Header';
import Results from './Results';
import BlogRequest from '../../../requests/api/blog';
import SuccessStoryRequest from '../../../requests/api/successStory';
import ClusterRequest from '../../../requests/api/cluster';
import { BLOG, SUCCESS_STORY } from '../../../utils/const/contentTypes';
import { BLOG as CLUSTER_BLOG, CASO_EXITO as CLUSTER_CASO_EXITO } from '../../../utils/const/clusterTypes';
import { DEFAULT_SORT } from './sortOptions';

const clusterTypeFor = (contentType) => (
  contentType === BLOG ? CLUSTER_BLOG : CLUSTER_CASO_EXITO
);

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.dark,
    minHeight: '100%',
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
  },
  tabs: {
    marginBottom: theme.spacing(2),
  },
}));

const COUNT_ONLY_LIMIT = 1;

function ListView() {
  const classes = useStyles();
  const location = useLocation();
  const initialTab = new URLSearchParams(location.search).get('tab') === SUCCESS_STORY
    ? SUCCESS_STORY
    : BLOG;
  const [contentType, setContentType] = useState(initialTab);
  const [rows, setRows] = useState([]);
  const [pageOfTable, setPageOfTable] = useState(0);
  const [limit, setLimit] = useState(10);
  const [searchValue, setSearchValue] = useState('');
  const [count, setCount] = useState(0);
  const [tabCounts, setTabCounts] = useState({ [BLOG]: 0, [SUCCESS_STORY]: 0 });
  const [clusters, setClusters] = useState([]);
  const [clusterId, setClusterId] = useState('');
  const [publishedFilter, setPublishedFilter] = useState('');
  const [featuredFilter, setFeaturedFilter] = useState('');
  const [sort, setSort] = useState(DEFAULT_SORT);

  useEffect(() => {
    const getClusters = async () => {
      const response = await ClusterRequest.getClusters(clusterTypeFor(contentType));
      if (response.data?.data) {
        setClusters(response.data.data);
      }
    };
    getClusters();
  }, [contentType]);

  useEffect(() => {
    const getTabCounts = async () => {
      const [blogsResponse, successStoriesResponse] = await Promise.all([
        BlogRequest.getBlogs({ page: 0, limit: COUNT_ONLY_LIMIT, searchValue: '' }),
        SuccessStoryRequest.getSuccessStories({ page: 0, limit: COUNT_ONLY_LIMIT, searchValue: '' }),
      ]);
      setTabCounts({
        [BLOG]: blogsResponse.success ? blogsResponse.data.data.count : 0,
        [SUCCESS_STORY]: successStoriesResponse.success
          ? successStoriesResponse.data.data.count
          : 0,
      });
    };
    getTabCounts();
  }, []);

  useEffect(() => {
    const getList = async () => {
      const response = contentType === BLOG
        ? await BlogRequest.getBlogs({
          page: pageOfTable,
          limit,
          searchValue,
          clusterId,
          published: publishedFilter,
          featured: featuredFilter,
          sort,
        })
        : await SuccessStoryRequest.getSuccessStories({
          page: pageOfTable,
          limit,
          searchValue,
          clusterId,
          published: publishedFilter,
          sort,
        });
      if (response.success) {
        const data = response.data.data.rows;
        setRows(data);
        setCount(response.data.data.count);
        // El contador de la pestaña es el total, no el resultado filtrado
        const hasFilters = Boolean(searchValue || clusterId || publishedFilter !== '' || featuredFilter !== '');
        if (!hasFilters) {
          setTabCounts((prev) => ({ ...prev, [contentType]: response.data.data.count }));
        }
      }
    };
    getList();
  }, [contentType, pageOfTable, limit, searchValue, clusterId, publishedFilter, featuredFilter, sort]);

  const handleTabChange = (event, newContentType) => {
    setContentType(newContentType);
    setPageOfTable(0);
    setSearchValue('');
    setClusterId('');
    setPublishedFilter('');
    setFeaturedFilter('');
    setSort(DEFAULT_SORT);
  };

  return (
    <Page
      className={classes.root}
      title="TubeOps | Blogs"
    >
      <Container maxWidth={false}>
        <Header contentType={contentType} />
        <Tabs
          className={classes.tabs}
          indicatorColor="secondary"
          onChange={handleTabChange}
          scrollButtons="auto"
          textColor="secondary"
          value={contentType}
          variant="scrollable"
        >
          <Tab label={`Blogs (${tabCounts[BLOG]})`} value={BLOG} />
          <Tab label={`Casos de Éxito (${tabCounts[SUCCESS_STORY]})`} value={SUCCESS_STORY} />
        </Tabs>
        <Box mt={3}>
          <Results
            contentType={contentType}
            rows={rows}
            pageOfTable={pageOfTable}
            setPageOfTable={setPageOfTable}
            limit={limit}
            setLimit={setLimit}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            count={count}
            clusters={clusters}
            clusterId={clusterId}
            setClusterId={setClusterId}
            publishedFilter={publishedFilter}
            setPublishedFilter={setPublishedFilter}
            featuredFilter={featuredFilter}
            setFeaturedFilter={setFeaturedFilter}
            sort={sort}
            setSort={setSort}
          />
        </Box>
      </Container>
    </Page>
  );
}

export default ListView;
